import type { ApiError } from '../types';

export const BASE = '/api';

export class ApiResponseError extends Error {
  status: number;
  code: string | undefined;

  constructor(status: number, detail: string, code?: string) {
    super(detail);
    this.name = 'ApiResponseError';
    this.status = status;
    this.code = code;
  }
}

// FastAPI 422 returns `detail` as an array of structured validation
// errors, not a string. We coerce that into a single human-readable
// string so callers can surface it without rendering "[object Object]".
function formatFastApiValidationDetail(detail: unknown): string {
  if (Array.isArray(detail)) {
    const lines = detail
      .map((err) => {
        if (!err || typeof err !== 'object') return String(err);
        const e = err as { loc?: unknown[]; msg?: string };
        const loc = Array.isArray(e.loc)
          ? e.loc
              .filter((p) => p !== 'body' && p !== 'query' && p !== 'path')
              .join('.')
          : '';
        const msg = e.msg ?? 'invalid';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return lines.join('; ') || 'Validation failed.';
  }
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      return 'Request failed.';
    }
  }
  return 'Request failed.';
}

async function parseErrorBody(res: Response): Promise<ApiError> {
  try {
    // FastAPI's HTTPException uses {detail}; the app's PrismError handlers
    // use {error, message}. Accept either so backend error messages surface.
    const body = (await res.json()) as {
      detail?: unknown;
      message?: unknown;
      code?: string;
      error?: string;
    };
    const raw = body.detail ?? body.message;
    return {
      detail: formatFastApiValidationDetail(raw),
      code: body.code ?? body.error,
    };
  } catch {
    return { detail: res.statusText || `HTTP ${res.status}` };
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });

  if (!res.ok) {
    const err = await parseErrorBody(res);
    throw new ApiResponseError(res.status, err.detail, err.code);
  }

  if (res.status === 204) {
    return undefined as unknown as T;
  }

  return res.json() as Promise<T>;
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function del<T>(path: string, body?: unknown): Promise<T> {
  // DELETE may carry a body (e.g. typed-confirmation payloads). Spec
  // technically allows it; some intermediaries strip it but our same-
  // origin path through Caddy preserves it.
  return request<T>(path, {
    method: 'DELETE',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

// Multipart upload — no Content-Type header so the browser sets boundary
export async function uploadFile<T>(
  path: string,
  file: File,
  fieldName = 'file'
): Promise<T> {
  const form = new FormData();
  form.append(fieldName, file);

  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  if (!res.ok) {
    const err = await parseErrorBody(res);
    throw new ApiResponseError(res.status, err.detail, err.code);
  }

  return res.json() as Promise<T>;
}

// Trigger a file download (e.g. CSV export) from an authenticated endpoint
export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
  });

  if (!res.ok) {
    const err = await parseErrorBody(res);
    throw new ApiResponseError(res.status, err.detail, err.code);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// Build a query string from an object, omitting undefined/null values
export function buildQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}
